import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Trash2, Edit, ExternalLink, AlertTriangle, Info, Calendar, BarChart, CheckCircle, HelpCircle } from 'lucide-react';

const PolicyList = ({ status = 'live', limit = 50, isAdmin = false }) => {
  const [policies, setPolicies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [updatingStatus, setUpdatingStatus] = useState(null);
  
  // Fetch policies with the given status
  const fetchPolicies = async (page = 0, search = '') => {
    setLoading(true);
    setError(null);
    
    try {
      // Simplified URL with no parameters for now to fix 500 error
      const url = `/api/policies/list`;
      console.log("Fetching policies from:", url);
      
      const response = await fetch(url);
      console.log("Response status:", response.status);
      
      if (!response.ok) {
        throw new Error(`Error fetching policies: ${response.statusText}`);
      }
      
      const data = await response.json();
      console.log("Received policies:", data.policies?.length || 0);
      
      if (data.success) {
        // Filter policies client-side based on status parameter
        let filteredPolicies = data.policies || [];
        
        if (status !== 'all') {
          if (status === 'live') {
            // For live view, show only live
            filteredPolicies = filteredPolicies.filter(p => 
              p.status === 'live'
            );
          } else {
            filteredPolicies = filteredPolicies.filter(p => p.status === status);
          }
        }
        
        // Filter by search term if provided
        if (search) {
          const searchLower = search.toLowerCase();
          filteredPolicies = filteredPolicies.filter(p => 
            (p.title && p.title.toLowerCase().includes(searchLower)) || 
            (p.summary && p.summary.toLowerCase().includes(searchLower))
          );
        }
        
        // Apply pagination
        const offset = page * limit;
        const paginatedPolicies = filteredPolicies.slice(offset, offset + limit);
        
        setPolicies(paginatedPolicies);
        setTotalCount(filteredPolicies.length);
      } else {
        throw new Error(data.error || 'Failed to fetch policies');
      }
    } catch (err) {
      console.error('Error fetching policies:', err);
      setError(err.message || 'Failed to load policies');
    } finally {
      setLoading(false);
    }
  };
  
  // Handle policy deletion
  const handleDelete = async (policyId) => {
    if (!policyId) return;
    
    // Show deleting indicator or disable further actions
    setUpdatingStatus(`deleting-${policyId}`);
    
    try {
      console.log(`Deleting policy with ID: ${policyId}`);
      const response = await fetch(`/api/policies/${policyId}`, {
        method: 'DELETE'
      });
      
      console.log(`Delete response status: ${response.status}`);
      
      if (!response.ok) {
        throw new Error(`Error deleting policy: ${response.status} ${response.statusText}`);
      }
      
      let data;
      try {
        data = await response.json();
      } catch (parseError) {
        console.error('Error parsing delete response:', parseError);
        throw new Error('Invalid response from server');
      }
      
      console.log('Delete response data:', data);
      
      if (data.success) {
        // Refresh the policy list after deletion
        fetchPolicies(currentPage, searchTerm);
      } else {
        throw new Error(data.error || 'Failed to delete policy');
      }
    } catch (err) {
      console.error('Error deleting policy:', err);
      setError(`Failed to delete policy: ${err.message}`);
    } finally {
      setUpdatingStatus(null);
    }
  };
  
  // Handle toggling policy status
  const handleToggleStatus = async (policy) => {
    if (!policy || !policy.id) return;
    
    setUpdatingStatus(policy.id);
    
    try {
      // Determine the new status based on current status
      let newStatus;
      switch (policy.status) {
        case 'draft':
          newStatus = 'analyzed';
          break;
        case 'analyzed':
          newStatus = 'live';
          break;
        case 'live':
          newStatus = 'analyzed';
          break;
        default:
          newStatus = 'analyzed';
      }
      
      const response = await fetch(`/api/policies/${policy.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          status: newStatus,
          // If publishing, set published_at timestamp
          ...(newStatus === 'live' ? { published_at: new Date().toISOString() } : {})
        })
      });
      
      if (!response.ok) {
        throw new Error(`Error updating policy status: ${response.statusText}`);
      }
      
      const data = await response.json();
      
      if (data.success) {
        // Update the policy in the local state
        setPolicies(policies.map(p => 
          p.id === policy.id ? { ...p, status: newStatus } : p
        ));
        
        // If we're viewing a filtered list, we might need to refresh
        if (status !== 'all') {
          fetchPolicies(currentPage, searchTerm);
        }
      } else {
        throw new Error(data.error || 'Failed to update policy status');
      }
    } catch (err) {
      console.error('Error toggling policy status:', err);
      setError(`Failed to update policy status: ${err.message}`);
    } finally {
      setUpdatingStatus(null);
    }
  };
  
  // Initial fetch on component mount
  useEffect(() => {
    fetchPolicies();
  }, [status, limit]);
  
  // Handle search
  const handleSearch = (e) => {
    e.preventDefault();
    setCurrentPage(0); // Reset to first page when searching
    fetchPolicies(0, searchTerm);
  };
  
  // Handle page changes
  const handlePageChange = (page) => {
    setCurrentPage(page);
    fetchPolicies(page, searchTerm);
  };
  
  // Get threat level color - using slate color scheme for consistency with articles
  const getThreatLevelColor = (score) => {
    if (score >= 81) return 'bg-slate-300 text-slate-800 border-slate-400';
    if (score >= 61) return 'bg-slate-300 text-slate-800 border-slate-400';
    if (score >= 41) return 'bg-slate-200 text-slate-800 border-slate-300';
    if (score >= 21) return 'bg-slate-200 text-slate-800 border-slate-300';
    return 'bg-slate-100 text-slate-800 border-slate-200';
  };
  
  // Get threat level label
  const getThreatLevelLabel = (score) => {
    if (score >= 81) return 'Severe';
    if (score >= 61) return 'Significant';
    if (score >= 41) return 'Moderate';
    if (score >= 21) return 'Mild';
    return 'Minimal';
  };
  
  // Get score color class - using same colors as ArticleAnalysisCard for consistency
  const getScoreColorClass = (score) => {
    if (score === null || score === undefined || score === 0) return 'bg-gray-200 text-gray-700';
    if (score <= 30) return 'bg-slate-200 text-slate-800';
    if (score <= 60) return 'bg-slate-300 text-slate-800';
    return 'bg-slate-400 text-slate-800';
  };
  
  // Get progress bar color based on score - using same colors as ArticleAnalysisCard for consistency
  const getProgressBarColorClass = (score) => {
    if (score === null || score === undefined || score === 0) return 'bg-gray-400';
    if (score <= 30) return 'bg-slate-400';
    if (score <= 60) return 'bg-slate-500';
    return 'bg-slate-500';
  };
  
  // Truncate text with ellipsis
  const truncateText = (text, maxLength = 180) => {
    if (!text) return '';
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };
  
  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return 'No date';
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
    } catch (e) {
      return dateStr;
    }
  };
  
  // Generate a URL-friendly slug from the title
  const generateSlug = (text) => {
    if (!text) return '';
    
    // Generate a clean slug from the title
    return text
      .toLowerCase()
      .replace(/[^\w\s-]/g, '') // Remove special characters
      .replace(/\s+/g, '-')     // Replace spaces with hyphens
      .substring(0, 50)         // Limit length
      .replace(/-+$/, '');      // Remove trailing dashes
  };
  
  if (loading && policies.length === 0) {
    return (
      <div className="flex justify-center items-center min-h-80">
        <div className="animate-pulse flex space-x-4">
          <div className="text-gray-600">Loading policies...</div>
        </div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded mb-4">
        <p className="font-bold">Error loading policies</p>
        <p>{error}</p>
      </div>
    );
  }
  
  if (policies.length === 0) {
    return (
      <div className="bg-gray-50 border border-gray-200 text-gray-800 px-4 py-5 rounded mb-4 text-center">
        <p className="text-lg font-medium">No policies found</p>
        <p className="text-gray-600">Try adjusting your filters or check back later for more policy analyses.</p>
      </div>
    );
  }
  
  return (
    <div className="w-full">
      
      {/* Error message */}
      {error && (
        <div className="p-4 mb-4 bg-red-50 border-l-4 border-red-500 text-red-700">
          <div className="flex">
            <AlertTriangle className="h-5 w-5 mr-2" />
            <span>{error}</span>
          </div>
        </div>
      )}
      
      {/* Policy list container */}
      
      {/* Policies list */}
      <div className="grid grid-cols-1 gap-6 mb-6">
        {policies.map(policy => (
          <div 
            key={policy.id} 
            className="bg-white shadow-md rounded-lg overflow-hidden border max-w-4xl mx-auto md:grid md:grid-cols-12 md:gap-0 hover:shadow-lg transition-shadow"
          >
            {/* Mobile Layout */}
            <div className="block md:hidden">
              <div className="p-4">
                <h3 className="font-bold text-lg mb-2 line-clamp-2">
                  <Link 
                    to={isAdmin ? `/admin/policies/${policy.id}/edit` : `/p/${generateSlug(policy.title)}-${policy.id}.html`} 
                    className="hover:text-blue-600"
                    title={`View policy: ${policy.title}`}
                  >
                    {policy.title}
                  </Link>
                </h3>
                
                <div className="flex flex-wrap text-xs text-gray-600 mb-3">
                  {policy.source_link && (
                    <div className="mr-3">
                      <a 
                        href={policy.source_link} 
                        target="_blank" 
                        rel="noopener noreferrer nofollow"
                        className="text-blue-600 hover:text-blue-800 font-medium"
                      >
                        View Original Source
                      </a>
                    </div>
                  )}
                  <div>
                    <span className="font-medium">Date:</span> {formatDate(policy.date)}
                  </div>
                </div>
              </div>
              
              {/* Score and Stats Section */}
              <div className="p-4 bg-gray-100">
                {/* Threat Level Badge */}
                <div className="mb-3 bg-gray-50 p-2 rounded border border-gray-100">
                  <div className="flex items-center justify-between">
                    <p className="text-sm text-gray-800">
                      <span className="font-medium">Analysis:</span> Rated as{' '}
                      <span className={`font-bold ${
                        policy.score <= 30 ? 'text-slate-700' : 
                        policy.score <= 60 ? 'text-slate-700' : 
                        'text-slate-700'
                      }`}>
                        {getThreatLevelLabel(policy.score)} Threat
                      </span>
                    </p>
                    <div className={`text-sm font-bold px-2 py-1 rounded-full ${getScoreColorClass(policy.score)}`}>
                      {policy.score}/100
                    </div>
                  </div>
                </div>
                
                {/* Individual Score Bars */}
                <div className="w-full space-y-2 mb-4">
                  <div>
                    <div className="flex justify-between text-xs mb-1">
                      <span className="font-medium text-gray-700">Authoritarian Tactics</span>
                      <span className="font-medium">{policy.score_tactics || '0'}/100</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div className={`h-2 rounded-full ${getProgressBarColorClass(policy.score_tactics)}`} 
                           style={{ width: `${policy.score_tactics || 0}%` }}></div>
                    </div>
                  </div>
                  
                  <div>
                    <div className="flex justify-between text-xs mb-1">
                      <span className="font-medium text-gray-700">Constitutional Issues</span>
                      <span className="font-medium">{policy.score_constitutional || '0'}/100</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div className={`h-2 rounded-full ${getProgressBarColorClass(policy.score_constitutional)}`} 
                           style={{ width: `${policy.score_constitutional || 0}%` }}></div>
                    </div>
                  </div>
                  
                  <div>
                    <div className="flex justify-between text-xs mb-1">
                      <span className="font-medium text-gray-700">Legal Precedents</span>
                      <span className="font-medium">{policy.score_precedents || '0'}/100</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div className={`h-2 rounded-full ${getProgressBarColorClass(policy.score_precedents)}`} 
                           style={{ width: `${policy.score_precedents || 0}%` }}></div>
                    </div>
                  </div>
                </div>
                
                {/* Summary */}
                {policy.summary && (
                  <div className="mb-3">
                    <h4 className="font-semibold text-xs text-gray-700 mb-1">Summary:</h4>
                    <p className="text-gray-800 line-clamp-4">{policy.summary}</p>
                  </div>
                )}
                
                {/* Stats Badges */}
                <div className="flex flex-wrap gap-2 mb-4">
                  <div className="bg-red-50 text-xs px-2 py-1 rounded-full border border-red-100">
                    <span className="text-red-700 font-medium">Authoritarian Tactics:</span>
                    <span className="ml-1">{policy.tactics?.length || 0}</span>
                  </div>
                  <div className="bg-blue-50 text-xs px-2 py-1 rounded-full border border-blue-100">
                    <span className="text-blue-700 font-medium">Constitutional Issues:</span>
                    <span className="ml-1">{policy.constitutional_issues?.length || 0}</span>
                  </div>
                  <div className="bg-purple-50 text-xs px-2 py-1 rounded-full border border-purple-100">
                    <span className="text-purple-700 font-medium">Legal Precedents:</span>
                    <span className="ml-1">{policy.precedents?.length || 0}</span>
                  </div>
                  
                  {isAdmin && (
                    <div className={`text-xs font-medium px-2 py-1 rounded-full border ${
                      policy.status === 'draft' ? 'bg-gray-100 text-gray-800 border-gray-200' :
                      policy.status === 'analyzed' ? 'bg-blue-100 text-blue-800 border-blue-200' :
                      policy.status === 'live' ? 'bg-green-100 text-green-800 border-green-200' :
                      'bg-gray-100 text-gray-800 border-gray-200'
                    }`}>
                      {policy.status === 'draft' ? 'Draft' :
                      policy.status === 'analyzed' ? 'Analyzed' :
                      policy.status === 'live' ? 'Published' : 
                      policy.status}
                    </div>
                  )}
                </div>
                
                {/* View full analysis link */}
                <div className="flex justify-between mt-2">
                  <Link
                    to={isAdmin ? `/admin/policies/${policy.id}/edit` : `/p/${generateSlug(policy.title)}-${policy.id}.html`}
                    className="inline-block bg-slate-100 hover:bg-slate-200 text-blue-600 text-sm font-medium py-1.5 px-3 rounded border border-slate-300"
                    title={`${isAdmin ? 'Edit' : 'View'} policy: ${policy.title}`}
                  >
                    {isAdmin ? 'Edit Policy' : 'View Full Analysis'}
                  </Link>
                  
                  {isAdmin && (
                    <div className="flex gap-2">
                      <button 
                        onClick={() => window.confirm('Are you sure you want to delete this policy?') && handleDelete(policy.id)}
                        disabled={updatingStatus === `deleting-${policy.id}`}
                        className="inline-flex items-center bg-slate-100 hover:bg-slate-200 text-red-600 text-sm font-medium py-1.5 px-3 rounded border border-slate-300"
                        title="Delete policy"
                      >
                        {updatingStatus === `deleting-${policy.id}` ? (
                          <div className="h-4 w-4 animate-spin rounded-full border-2 border-red-500 border-t-transparent" />
                        ) : (
                          <Trash2 className="h-4 w-4" />
                        )}
                      </button>
                      <button 
                        onClick={() => handleToggleStatus(policy)}
                        disabled={updatingStatus === policy.id}
                        className={`inline-flex items-center bg-slate-100 hover:bg-slate-200 text-sm font-medium py-1.5 px-3 rounded border border-slate-300 ${
                          policy.status === 'live' ? 'text-amber-600' : 
                          policy.status === 'analyzed' ? 'text-green-600' : 
                          'text-blue-600'
                        }`}
                        title={
                          policy.status === 'live' ? 'Unpublish policy' : 
                          policy.status === 'analyzed' ? 'Publish policy' : 
                          'Mark as analyzed'
                        }
                      >
                        {updatingStatus === policy.id ? (
                          <div className="h-4 w-4 animate-spin rounded-full border-2 border-gray-500 border-t-transparent" />
                        ) : (
                          <CheckCircle className={`h-4 w-4 ${
                            policy.status === 'live' ? 'fill-green-500' : 
                            policy.status === 'analyzed' ? 'stroke-blue-500' : 
                            'stroke-gray-500'
                          }`} />
                        )}
                        <span className="ml-1">
                          {policy.status === 'live' ? 'Unpublish' : 
                           policy.status === 'analyzed' ? 'Publish' : 
                           'Approve'}
                        </span>
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
            
            {/* Desktop Layout - Score display on left, policy details on right */}
            <div className="hidden md:flex md:col-span-3 bg-gray-100 flex-col justify-center items-center p-4">
              <div className="flex flex-col items-center w-full">
                <div className="text-center mb-2">
                  <div className="text-lg font-bold text-gray-800">Threat Score</div>
                  <div className="flex items-center justify-center text-sm text-gray-500">
                    <span>Higher = Greater Threat</span>
                    <div className="relative group ml-1">
                      <HelpCircle className="h-4 w-4 text-gray-400 cursor-help" />
                      <div className="absolute z-10 hidden group-hover:block bg-white p-2 rounded shadow-lg border border-gray-200 w-48 text-xs text-gray-700 bottom-full left-1/2 transform -translate-x-1/2 mb-1">
                        Our threat scores range from 1-100. Higher scores indicate greater threats to democratic institutions.
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Overall score badge */}
                <div className={`text-center font-bold text-3xl rounded-full w-20 h-20 flex items-center justify-center mb-4 ${
                  policy.score <= 30 ? 'bg-slate-100 text-slate-700 border-2 border-slate-300' : 
                  policy.score <= 60 ? 'bg-slate-200 text-slate-700 border-2 border-slate-300' : 
                  'bg-slate-100 text-slate-700 border-2 border-slate-300'
                }`}>
                  {policy.score || '?'}
                </div>
                
                {/* Individual score bars */}
                <div className="w-full space-y-2">
                  <div>
                    <div className="flex justify-between text-xs mb-1">
                      <span className="font-medium text-gray-700">Authoritarian Tactics</span>
                      <span className="font-medium">{policy.score_tactics || '0'}</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div className={`h-2 rounded-full ${getProgressBarColorClass(policy.score_tactics)}`} 
                          style={{ width: `${policy.score_tactics || 0}%` }}></div>
                    </div>
                  </div>
                  
                  <div>
                    <div className="flex justify-between text-xs mb-1">
                      <span className="font-medium text-gray-700">Constitutional Issues</span>
                      <span className="font-medium">{policy.score_constitutional || '0'}</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div className={`h-2 rounded-full ${getProgressBarColorClass(policy.score_constitutional)}`} 
                          style={{ width: `${policy.score_constitutional || 0}%` }}></div>
                    </div>
                  </div>
                  
                  <div>
                    <div className="flex justify-between text-xs mb-1">
                      <span className="font-medium text-gray-700">Legal Precedents</span>
                      <span className="font-medium">{policy.score_precedents || '0'}</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div className={`h-2 rounded-full ${getProgressBarColorClass(policy.score_precedents)}`} 
                          style={{ width: `${policy.score_precedents || 0}%` }}></div>
                    </div>
                  </div>
                </div>
                
                {/* Status badge for admin */}
                {isAdmin && (
                  <div className="mt-4">
                    <div className={`text-xs font-medium px-3 py-1.5 rounded-full border ${
                      policy.status === 'draft' ? 'bg-gray-100 text-gray-800 border-gray-200' :
                      policy.status === 'analyzed' ? 'bg-blue-100 text-blue-800 border-blue-200' :
                      policy.status === 'live' ? 'bg-green-100 text-green-800 border-green-200' :
                      'bg-gray-100 text-gray-800 border-gray-200'
                    }`}>
                      {policy.status === 'draft' ? 'Draft' :
                      policy.status === 'analyzed' ? 'Analyzed' :
                      policy.status === 'live' ? 'Published' : 
                      policy.status}
                    </div>
                  </div>
                )}
              </div>
            </div>
            
            {/* Desktop Policy Info */}
            <div className="hidden md:block md:col-span-9 p-4">
              <h3 className="font-bold text-lg mb-2 line-clamp-2">
                <Link 
                  to={isAdmin ? `/admin/policies/${policy.id}/edit` : `/p/${generateSlug(policy.title)}-${policy.id}.html`} 
                  className="hover:text-blue-600"
                  title={`View policy: ${policy.title}`}
                >
                  {policy.title}
                </Link>
              </h3>
              
              <div className="flex flex-wrap text-xs text-gray-600 mb-3">
                {policy.source_link && (
                  <div className="mr-3">
                    <a 
                      href={policy.source_link} 
                      target="_blank" 
                      rel="noopener noreferrer nofollow"
                      className="text-blue-600 hover:text-blue-800 font-medium"
                    >
                      View Original Source
                    </a>
                  </div>
                )}
                <div>
                  <span className="font-medium">Date:</span> {formatDate(policy.date)}
                </div>
              </div>
              
              {/* Threat Level Explanation */}
              <div className="mb-3 bg-gray-50 p-2 rounded border border-gray-100">
                <div className="flex items-center justify-between">
                  <p className="text-sm text-gray-800">
                    <span className="font-medium">Analysis:</span> Rated as{' '}
                    <span className={`font-bold ${
                      policy.score <= 30 ? 'text-slate-700' : 
                      policy.score <= 60 ? 'text-slate-700' : 
                      'text-slate-700'
                    }`}>
                      {getThreatLevelLabel(policy.score)} Threat to Democracy
                    </span>
                  </p>
                  <div className={`text-sm font-bold px-2 py-1 rounded-full ${getScoreColorClass(policy.score)}`}>
                    {policy.score}/100
                  </div>
                </div>
              </div>
              
              {/* Stats in Grid */}
              <div className="grid grid-cols-3 gap-2 mb-3">
                <div className="p-2 bg-slate-50 rounded border border-slate-200">
                  <div className="flex items-center justify-between mb-1">
                    <div className="text-xs uppercase font-semibold text-slate-700">Authoritarian Tactics</div>
                    <div className={`text-xs font-bold px-1.5 py-0.5 rounded-full ${getScoreColorClass(policy.score_tactics)}`}>
                      {policy.score_tactics || '0'}
                    </div>
                  </div>
                  <div className="w-full bg-slate-200 rounded-full h-2">
                    <div className={`h-2 rounded-full ${getProgressBarColorClass(policy.score_tactics)}`} 
                        style={{ width: `${policy.score_tactics || 0}%` }}></div>
                  </div>
                </div>
                
                <div className="p-2 bg-slate-50 rounded border border-slate-200">
                  <div className="flex items-center justify-between mb-1">
                    <div className="text-xs uppercase font-semibold text-slate-700">Constitutional Issues</div>
                    <div className={`text-xs font-bold px-1.5 py-0.5 rounded-full ${getScoreColorClass(policy.score_constitutional)}`}>
                      {policy.score_constitutional || '0'}
                    </div>
                  </div>
                  <div className="w-full bg-slate-200 rounded-full h-2">
                    <div className={`h-2 rounded-full ${getProgressBarColorClass(policy.score_constitutional)}`} 
                        style={{ width: `${policy.score_constitutional || 0}%` }}></div>
                  </div>
                </div>
                
                <div className="p-2 bg-slate-50 rounded border border-slate-200">
                  <div className="flex items-center justify-between mb-1">
                    <div className="text-xs uppercase font-semibold text-slate-700">Legal Precedents</div>
                    <div className={`text-xs font-bold px-1.5 py-0.5 rounded-full ${getScoreColorClass(policy.score_precedents)}`}>
                      {policy.score_precedents || '0'}
                    </div>
                  </div>
                  <div className="w-full bg-slate-200 rounded-full h-2">
                    <div className={`h-2 rounded-full ${getProgressBarColorClass(policy.score_precedents)}`} 
                        style={{ width: `${policy.score_precedents || 0}%` }}></div>
                  </div>
                </div>
              </div>
              
              {/* Summary */}
              {policy.summary && (
                <div className="mb-3">
                  <h4 className="font-semibold text-xs text-gray-700 mb-1">Summary:</h4>
                  <p className="text-gray-800 line-clamp-4">{policy.summary}</p>
                </div>
              )}
              
              {/* Actions */}
              <div className="flex justify-between mt-2">
                <Link
                  to={isAdmin ? `/admin/policies/${policy.id}/edit` : `/p/${generateSlug(policy.title)}-${policy.id}.html`}
                  className="inline-block bg-slate-100 hover:bg-slate-200 text-blue-600 text-sm font-medium py-1.5 px-3 rounded border border-slate-300"
                  title={`${isAdmin ? 'Edit' : 'View'} policy: ${policy.title}`}
                >
                  {isAdmin ? 'Edit Policy' : 'View Full Analysis'}
                </Link>
                
                {isAdmin && (
                  <div className="flex gap-2">
                    <button 
                      onClick={() => window.confirm('Are you sure you want to delete this policy?') && handleDelete(policy.id)}
                      disabled={updatingStatus === `deleting-${policy.id}`}
                      className="inline-flex items-center bg-slate-100 hover:bg-slate-200 text-red-600 text-sm font-medium py-1.5 px-3 rounded border border-slate-300"
                      title="Delete policy"
                    >
                      {updatingStatus === `deleting-${policy.id}` ? (
                        <div className="h-4 w-4 animate-spin rounded-full border-2 border-red-500 border-t-transparent" />
                      ) : (
                        <Trash2 className="h-4 w-4" />
                      )}
                    </button>
                    <button 
                      onClick={() => handleToggleStatus(policy)}
                      disabled={updatingStatus === policy.id}
                      className={`inline-flex items-center bg-slate-100 hover:bg-slate-200 text-sm font-medium py-1.5 px-3 rounded border border-slate-300 ${
                        policy.status === 'live' ? 'text-amber-600' : 
                        policy.status === 'analyzed' ? 'text-green-600' : 
                        'text-blue-600'
                      }`}
                      title={
                        policy.status === 'live' ? 'Unpublish policy' : 
                        policy.status === 'analyzed' ? 'Publish policy' : 
                        'Mark as analyzed'
                      }
                    >
                      {updatingStatus === policy.id ? (
                        <div className="h-4 w-4 animate-spin rounded-full border-2 border-gray-500 border-t-transparent" />
                      ) : (
                        <CheckCircle className={`h-4 w-4 ${
                          policy.status === 'live' ? 'fill-green-500' : 
                          policy.status === 'analyzed' ? 'stroke-blue-500' : 
                          'stroke-gray-500'
                        }`} />
                      )}
                      <span className="ml-1">
                        {policy.status === 'live' ? 'Unpublish' : 
                         policy.status === 'analyzed' ? 'Publish' : 
                         'Approve'}
                      </span>
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
      
      {/* Pagination */}
      {totalCount > limit && (
        <div className="flex justify-center mt-6">
          <nav className="inline-flex rounded-md shadow" aria-label="Pagination">
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 0}
              aria-label="Previous page"
              className={`px-3 py-1 rounded-l-md border border-gray-300 bg-white text-sm font-medium ${
                currentPage === 0
                  ? 'text-gray-300 cursor-not-allowed'
                  : 'text-gray-700 hover:bg-gray-50'
              }`}
            >
              Previous
            </button>
            
            <div className="px-4 py-1 border-t border-b border-gray-300 bg-white text-sm pagination-info">
              Page {currentPage + 1} of {Math.ceil(totalCount / limit)}
            </div>
            
            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage >= Math.ceil(totalCount / limit) - 1}
              aria-label="Next page"
              className={`px-3 py-1 rounded-r-md border border-gray-300 bg-white text-sm font-medium ${
                currentPage >= Math.ceil(totalCount / limit) - 1
                  ? 'text-gray-300 cursor-not-allowed'
                  : 'text-gray-700 hover:bg-gray-50'
              }`}
            >
              Next
            </button>
          </nav>
        </div>
      )}
    </div>
  );
};

export default PolicyList;